import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://esjrycmiokijtxnbfyox.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVzanJ5Y21pb2tpanR4bmJmeW94Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQ2NDc3OTIsImV4cCI6MjA3MDIyMzc5Mn0.mBXxIiC0cWzPufhvS6TyvZTC9H4AcxN0ljRYD-caEVk';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
  console.error('Required: VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY');
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Storage bucket names
export const STORAGE_BUCKETS = {
  LOGOS: 'token-logos',
  COVERS: 'token-covers',
  CARDS: 'token-cards',
  STATIC: 'static-assets',
  WIDGET_LOGOS: 'widget_custom_user_logos',
  MONAD_LOGOS: 'monad-logos',
  ETH_LOGOS: 'eth-token-logos',
  SOLANA_LOGOS: 'solana-logos'
} as const;

// Helper function to upload image to Supabase Storage
export const uploadImageToStorage = async (
  file: File,
  bucket: string,
  fileName: string
): Promise<string | null> => {
  try {
    console.log(`📤 Attempting to upload "${fileName}" to bucket "${bucket}"`);

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) {
      console.error(`❌ Supabase upload error (bucket: "${bucket}"):`, error);
      if (error.message?.includes('Bucket not found')) {
        console.error(`💡 The bucket "${bucket}" does not exist in your Supabase project.`);
        console.error(`💡 Please create the bucket "${bucket}" in your Supabase Storage dashboard.`);
      }
      return null;
    }

    console.log(`✅ Upload successful, path:`, data?.path);

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(fileName);

    return urlData.publicUrl;
  } catch (error) {
    console.error(`❌ Error uploading image to Supabase Storage (bucket: "${bucket}"):`, error);
    return null;
  }
};

// Helper function to upload base64 image to Supabase Storage
export const uploadBase64ImageToStorage = async (
  base64Data: string,
  bucket: string,
  fileName: string,
  mimeType: string = 'image/png'
): Promise<string | null> => {
  try {
    // Convert base64 to blob
    const response = await fetch(`data:${mimeType};base64,${base64Data}`);
    const blob = await response.blob();

    // Create file from blob
    const file = new File([blob], fileName, { type: mimeType });

    // Upload to Supabase Storage
    return await uploadImageToStorage(file, bucket, fileName);
  } catch (error) {
    console.error('Error uploading base64 image to Supabase Storage:', error);
    return null;
  }
};

// Helper function to upload blob to Supabase Storage
export const uploadBlobToStorage = async (
  blob: Blob,
  bucket: string,
  fileName: string
): Promise<string | null> => {
  try {
    console.log(`📤 Uploading blob to bucket "${bucket}", file: "${fileName}"`);

    // Create file from blob
    const file = new File([blob], fileName, { type: 'image/png' });

    // Upload to Supabase Storage
    const url = await uploadImageToStorage(file, bucket, fileName);

    if (url) {
      console.log(`✅ Upload successful: ${url}`);
    } else {
      console.error(`❌ Upload failed - no URL returned for bucket "${bucket}"`);
    }

    return url;
  } catch (error) {
    console.error(`❌ Error uploading blob to Supabase Storage (bucket: "${bucket}"):`, error);
    return null;
  }
};

// Database table names
export const TABLES = {
  TOKENS: 'tokens',
  USERS: 'users',
  USER_FAVORITES: 'user_favorites',
  WEB_SWAP: 'web_swap',
  USER_WEB_ACCOUNTS: 'user_web_accounts',
  USERS_HOLDINGS: 'users_holdings',
  MAVRKPAY_TRANSACTIONS: 'mavrkpay_transactions',
  TOKEN_COMMENTS: 'token_comments',
  WIDGET_USAGE: 'widget_usage',
  TOKENS_SOL: 'tokens_sol',
  TOKENS_SOL_AGENTS: 'tokens_sol_agents',
  FEEDBACK: 'feedback',
  TOKENS_USD1: 'tokens_usd1', // WLFI USD1 paired tokens on Ethereum mainnet
  TOKENS_ETH: 'tokens_eth', // ETH section tokens on Ethereum mainnet (USDC/WETH paired)
  INVESTORS: 'investors',
  INVESTOR_FEEDBACK: 'investor_feedback',
  INVESTOR_ANNOUNCEMENTS: 'investor_announcements',
  INVESTOR_ADMINS: 'investor_admins'
} as const;

export const INVESTOR_SLOT_FIELDS = Array.from({ length: 20 }, (_, i) => `token_slot_${i + 1}`);

// Helper function to log swap button clicks for airdrop tracking
export const logSwapClick = async (
  userAddress: string,
  swapType: 'buy' | 'sell',
  tokenAddress?: string,
  tokenSymbol?: string,
  inputAmount?: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from(TABLES.WEB_SWAP)
      .insert({
        user_address: userAddress.toLowerCase(),
        swap_type: swapType,
        token_address: tokenAddress?.toLowerCase() || null,
        token_symbol: tokenSymbol || null,
        input_amount: inputAmount || null
      });

    if (error) {
      console.error('Error logging swap click:', error);
      return false;
    }

    console.log('✅ Swap click logged for airdrop tracking:', {
      user: userAddress,
      type: swapType,
      token: tokenSymbol
    });
    return true;
  } catch (error) {
    console.error('Error logging swap click to database:', error);
    return false;
  }
};

// Helper function to check if username exists
export const checkUsernameExists = async (username: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.USER_WEB_ACCOUNTS)
      .select('user_id')
      .eq('user_name', username)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
      console.error('Error checking username:', error);
      return false;
    }

    return !!data; // Returns true if username exists
  } catch (error) {
    console.error('Error checking username existence:', error);
    return false;
  }
};

// Helper function to create a new user account
export const createUserAccount = async (
  username: string,
  password: string,
  recoveryPhrase: string,
  walletAddress: string,
  encryptedPrivateKey: string
): Promise<{ success: boolean; userId?: string; error?: string }> => {
  try {
    // Check if username already exists
    const usernameExists = await checkUsernameExists(username);
    if (usernameExists) {
      return { success: false, error: 'Username already exists' };
    }

    // Create the user account
    const { data, error } = await supabase
      .from(TABLES.USER_WEB_ACCOUNTS)
      .insert({
        user_name: username,
        user_password: password, // ⚠️ SECURITY WARNING: Plain text password - NOT FOR PRODUCTION!
        user_recovery_phrase: recoveryPhrase,
        wallet_address: walletAddress,
        private_key_encrypted: encryptedPrivateKey
      })
      .select('user_id')
      .single();

    if (error) {
      console.error('Error creating user account:', error);
      return { success: false, error: 'Failed to create account' };
    }

    console.log('✅ User account created successfully:', {
      userId: data.user_id,
      username: username,
      walletAddress: walletAddress
    });

    return { success: true, userId: data.user_id };
  } catch (error) {
    console.error('Error creating user account:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

// Users Holdings helpers
export const getUserHoldings = async (walletAddress: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.USERS_HOLDINGS)
      .select('contract_address')
      .eq('wallet_address', walletAddress.toLowerCase());

    if (error) {
      console.error('Error fetching user holdings:', error);
      return [];
    }
    return (data || []).map((r: any) => (r.contract_address as string) || '').filter(Boolean);
  } catch (e) {
    console.error('Error fetching user holdings:', e);
    return [];
  }
};

export const addUserHolding = async (walletAddress: string, contractAddress: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from(TABLES.USERS_HOLDINGS)
      .insert({
        wallet_address: walletAddress.toLowerCase(),
        contract_address: contractAddress.toLowerCase(),
        added_at: new Date().toISOString()
      });
    if (error) {
      console.error('Error adding user holding:', error);
      return false;
    }
    return true;
  } catch (e) {
    console.error('Error adding user holding:', e);
    return false;
  }
};

export const removeUserHolding = async (walletAddress: string, contractAddress: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from(TABLES.USERS_HOLDINGS)
      .delete()
      .eq('wallet_address', walletAddress.toLowerCase())
      .eq('contract_address', contractAddress.toLowerCase());
    if (error) {
      console.error('Error removing user holding:', error);
      return false;
    }
    return true;
  } catch (e) {
    console.error('Error removing user holding:', e);
    return false;
  }
};

// Helper function to authenticate user login
export const authenticateUser = async (
  username: string,
  password: string
): Promise<{ success: boolean; user?: any; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.USER_WEB_ACCOUNTS)
      .select('user_id, user_name, wallet_address, user_recovery_phrase, user_password')
      .eq('user_name', username)
      .single();

    if (error) {
      console.error('Error authenticating user:', error);
      return { success: false, error: 'Invalid credentials' };
    }

    // ⚠️ SECURITY WARNING: Plain text password comparison - NOT FOR PRODUCTION!
    if (data.user_password !== password) {
      return { success: false, error: 'Invalid credentials' };
    }

    console.log('✅ User authenticated successfully:', {
      userId: data.user_id,
      username: data.user_name,
      walletAddress: data.wallet_address
    });

    return {
      success: true,
      user: {
        id: data.user_id,
        username: data.user_name,
        walletAddress: data.wallet_address,
        recoveryPhrase: data.user_recovery_phrase
      }
    };
  } catch (error) {
    console.error('Error authenticating user:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

export interface InvestorRecord {
  id: string;
  investor_name: string;
  investor_password: string;
  evm_address?: string | null;
  svm_address?: string | null;
  funding?: number | null;
  token_slot_1?: string | null;
  token_slot_2?: string | null;
  token_slot_3?: string | null;
  token_slot_4?: string | null;
  token_slot_5?: string | null;
  token_slot_6?: string | null;
  token_slot_7?: string | null;
  token_slot_8?: string | null;
  token_slot_9?: string | null;
  token_slot_10?: string | null;
  token_slot_11?: string | null;
  token_slot_12?: string | null;
  token_slot_13?: string | null;
  token_slot_14?: string | null;
  token_slot_15?: string | null;
  token_slot_16?: string | null;
  token_slot_17?: string | null;
  token_slot_18?: string | null;
  token_slot_19?: string | null;
  token_slot_20?: string | null;
  created_at?: string;
}

export interface InvestorFeedbackRecord {
  id?: string;
  investor_name: string;
  comment: string;
  created_at?: string;
}

export interface InvestorAnnouncementRecord {
  id?: string;
  investor_name?: string | null;
  message: string;
  created_at?: string;
}

export interface InvestorAdminRecord {
  id: string;
  admin_name: string;
  admin_password: string;
  created_at?: string;
}

export const authenticateInvestor = async (
  investorName: string,
  password: string
): Promise<{ success: boolean; investor?: InvestorRecord; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.INVESTORS)
      .select(`id, investor_name, investor_password, evm_address, svm_address, funding, created_at, ${INVESTOR_SLOT_FIELDS.join(',')}`)
      .ilike('investor_name', investorName)
      .single();

    if (error) {
      console.error('Error authenticating investor:', error);
      return { success: false, error: 'Invalid credentials' };
    }

    // ⚠️ SECURITY WARNING: Plain text password comparison - NOT FOR PRODUCTION!
    if (data.investor_password !== password) {
      return { success: false, error: 'Invalid credentials' };
    }

    return {
      success: true,
      investor: data
    };
  } catch (error) {
    console.error('Error authenticating investor:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

export const authenticateInvestorAdmin = async (
  adminName: string,
  password: string
): Promise<{ success: boolean; admin?: InvestorAdminRecord; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.INVESTOR_ADMINS)
      .select('id, admin_name, admin_password, created_at')
      .ilike('admin_name', adminName)
      .single();

    if (error) {
      console.error('Error authenticating admin:', error);
      return { success: false, error: 'Invalid credentials' };
    }

    // ⚠️ SECURITY WARNING: Plain text password comparison - NOT FOR PRODUCTION!
    if (data.admin_password !== password) {
      return { success: false, error: 'Invalid credentials' };
    }

    return {
      success: true,
      admin: data
    };
  } catch (error) {
    console.error('Error authenticating admin:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

export const getInvestorByName = async (
  investorName: string
): Promise<{ success: boolean; investor?: InvestorRecord; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.INVESTORS)
      .select(`id, investor_name, evm_address, svm_address, funding, created_at, ${INVESTOR_SLOT_FIELDS.join(',')}`)
      .ilike('investor_name', investorName)
      .single();

    if (error) {
      console.error('Error fetching investor:', error);
      return { success: false, error: 'Investor not found' };
    }

    return { success: true, investor: data };
  } catch (error) {
    console.error('Error fetching investor:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

export const createInvestorFeedback = async (
  investorName: string,
  comment: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const trimmed = comment.trim();
    if (!trimmed) {
      return { success: false, error: 'Feedback message is empty' };
    }
    if (trimmed.length > 150) {
      return { success: false, error: 'Feedback exceeds 150 characters' };
    }

    const { error } = await supabase
      .from(TABLES.INVESTOR_FEEDBACK)
      .insert({
        investor_name: investorName,
        comment: trimmed
      });

    if (error) {
      console.error('Error submitting feedback:', error);
      return { success: false, error: 'Failed to submit feedback' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error submitting feedback:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

export const getInvestorFeedback = async (
  investorName: string
): Promise<{ success: boolean; feedback?: InvestorFeedbackRecord[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.INVESTOR_FEEDBACK)
      .select('id, investor_name, comment, created_at')
      .eq('investor_name', investorName)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching feedback:', error);
      return { success: false, error: 'Failed to load feedback history' };
    }

    return { success: true, feedback: data || [] };
  } catch (error) {
    console.error('Error fetching feedback:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

export const getInvestorAnnouncements = async (
  investorName: string
): Promise<{ success: boolean; announcements?: InvestorAnnouncementRecord[]; error?: string }> => {
  try {
    let query = supabase
      .from(TABLES.INVESTOR_ANNOUNCEMENTS)
      .select('id, investor_name, message, created_at')
      .order('created_at', { ascending: false })
      .limit(50);

    if (investorName) {
      query = query.or(`investor_name.is.null,investor_name.eq.${investorName}`);
    } else {
      query = query.is('investor_name', null);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching announcements:', error);
      return { success: false, error: 'Failed to load announcements' };
    }

    return { success: true, announcements: data || [] };
  } catch (error) {
    console.error('Error fetching announcements:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

export const createInvestorAnnouncement = async (
  message: string,
  investorName?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const trimmed = message.trim();
    if (!trimmed) {
      return { success: false, error: 'Announcement message is empty' };
    }
    if (trimmed.length > 250) {
      return { success: false, error: 'Announcement exceeds 250 characters' };
    }

    const payload: { message: string; investor_name?: string | null } = {
      message: trimmed,
      investor_name: investorName?.trim() ? investorName.trim() : null
    };

    const { error } = await supabase
      .from(TABLES.INVESTOR_ANNOUNCEMENTS)
      .insert(payload);

    if (error) {
      console.error('Error creating announcement:', error);
      return { success: false, error: 'Failed to create announcement' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error creating announcement:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

export const getAllInvestors = async (): Promise<{ success: boolean; investors?: InvestorRecord[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.INVESTORS)
      .select(`id, investor_name, evm_address, svm_address, funding, created_at, ${INVESTOR_SLOT_FIELDS.join(',')}`)
      .order('investor_name', { ascending: true });

    if (error) {
      console.error('Error fetching investors:', error);
      return { success: false, error: 'Failed to load investors' };
    }

    return { success: true, investors: data || [] };
  } catch (error) {
    console.error('Error fetching investors:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

// Helper function to get user by recovery phrase (for password reset)
export const getUserByRecoveryPhrase = async (
  recoveryPhrase: string
): Promise<{ success: boolean; user?: any; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.USER_WEB_ACCOUNTS)
      .select('user_id, user_name, wallet_address')
      .eq('user_recovery_phrase', recoveryPhrase)
      .single();

    if (error) {
      console.error('Error finding user by recovery phrase:', error);
      return { success: false, error: 'Invalid recovery phrase' };
    }

    return {
      success: true,
      user: {
        id: data.user_id,
        username: data.user_name,
        walletAddress: data.wallet_address
      }
    };
  } catch (error) {
    console.error('Error finding user by recovery phrase:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

// Helper function to update user password
export const updateUserPassword = async (
  userId: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from(TABLES.USER_WEB_ACCOUNTS)
      .update({ user_password: newPassword }) // Note: In production, this should be bcrypt hashed
      .eq('user_id', userId);

    if (error) {
      console.error('Error updating password:', error);
      return { success: false, error: 'Failed to update password' };
    }

    console.log('✅ Password updated successfully for user:', userId);
    return { success: true };
  } catch (error) {
    console.error('Error updating password:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

// MavrkPay Transaction Logging Functions

/**
 * Log a MavrkPay transaction (deposit, pay, or request)
 */
export const logMavrkPayTransaction = async (
  userAddress: string,
  transactionType: 'deposit' | 'pay' | 'request',
  amount: string,
  counterpartyAddress?: string,
  txHash?: string,
  blockNumber?: number
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from(TABLES.MAVRKPAY_TRANSACTIONS)
      .insert({
        user_address: userAddress.toLowerCase(),
        transaction_type: transactionType,
        amount,
        counterparty_address: counterpartyAddress?.toLowerCase() || null,
        tx_hash: txHash || null,
        block_number: blockNumber || null,
      });

    if (error) {
      console.error('[MavrkPay] Error logging transaction:', error);
      return false;
    }

    console.log(`[MavrkPay] Logged ${transactionType} transaction:`, amount, 'USDG');
    return true;
  } catch (error) {
    console.error('[MavrkPay] Exception logging transaction:', error);
    return false;
  }
};

/**
 * Get MavrkPay transactions for a user
 */
export const getMavrkPayTransactions = async (
  userAddress: string,
  transactionType?: 'deposit' | 'pay' | 'request',
  limit: number = 10
): Promise<any[]> => {
  try {
    let query = supabase
      .from(TABLES.MAVRKPAY_TRANSACTIONS)
      .select('*')
      .or(`user_address.ilike.${userAddress},counterparty_address.ilike.${userAddress}`)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (transactionType) {
      query = query.eq('transaction_type', transactionType);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[MavrkPay] Error fetching transactions:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('[MavrkPay] Exception fetching transactions:', error);
    return [];
  }
};

// Widget Usage Tracking Functions

/**
 * Track widget load/embed event
 * Records when the widget is loaded on an external site
 */
export const trackWidgetLoad = async (
  referrer?: string,
  parentOrigin?: string,
  queryParams?: Record<string, string>
): Promise<boolean> => {
  try {
    // Get the embedding site's origin
    let embeddingSite = referrer || '';

    // Try to get parent origin if in iframe (may be blocked by CORS)
    if (!embeddingSite && typeof window !== 'undefined') {
      try {
        if (window.parent && window.parent !== window) {
          // Can't access window.parent.location due to CORS, but we can try
          embeddingSite = document.referrer || 'unknown';
        } else {
          embeddingSite = window.location.origin; // Direct access, not embedded
        }
      } catch (e) {
        // CORS blocked, use referrer
        embeddingSite = document.referrer || 'unknown';
      }
    }

    const { error } = await supabase
      .from(TABLES.WIDGET_USAGE)
      .insert({
        event_type: 'widget_load',
        embedding_site: embeddingSite,
        parent_origin: parentOrigin || null,
        query_params: queryParams || null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        timestamp: new Date().toISOString()
      });

    if (error) {
      console.error('[Widget Tracking] Error logging widget load:', error);
      return false;
    }

    console.log('[Widget Tracking] Logged widget load from:', embeddingSite);
    return true;
  } catch (error) {
    console.error('[Widget Tracking] Exception logging widget load:', error);
    return false;
  }
};

/**
 * Track token deployment from widget
 * Records when a token is successfully deployed via the widget
 */
export const trackWidgetDeployment = async (
  tokenAddress: string,
  tokenSymbol: string,
  tokenName: string,
  referrer?: string,
  parentOrigin?: string,
  queryParams?: Record<string, string>
): Promise<boolean> => {
  try {
    let embeddingSite = referrer || '';

    if (!embeddingSite && typeof window !== 'undefined') {
      try {
        if (window.parent && window.parent !== window) {
          embeddingSite = document.referrer || 'unknown';
        } else {
          embeddingSite = window.location.origin;
        }
      } catch (e) {
        embeddingSite = document.referrer || 'unknown';
      }
    }

    const { error } = await supabase
      .from(TABLES.WIDGET_USAGE)
      .insert({
        event_type: 'token_deployed',
        embedding_site: embeddingSite,
        parent_origin: parentOrigin || null,
        token_address: tokenAddress.toLowerCase(),
        token_symbol: tokenSymbol,
        token_name: tokenName,
        query_params: queryParams || null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        timestamp: new Date().toISOString()
      });

    if (error) {
      console.error('[Widget Tracking] Error logging widget deployment:', error);
      return false;
    }

    console.log('[Widget Tracking] Logged token deployment:', {
      token: tokenSymbol,
      site: embeddingSite
    });
    return true;
  } catch (error) {
    console.error('[Widget Tracking] Exception logging widget deployment:', error);
    return false;
  }
};

/**
 * Get widget usage statistics
 * Returns aggregated data about widget usage
 */
export const getWidgetUsageStats = async (
  startDate?: string,
  endDate?: string
): Promise<{
  totalLoads: number;
  totalDeployments: number;
  uniqueSites: number;
  sites: Array<{ site: string; loads: number; deployments: number }>;
}> => {
  try {
    let query = supabase
      .from(TABLES.WIDGET_USAGE)
      .select('*');

    if (startDate) {
      query = query.gte('timestamp', startDate);
    }
    if (endDate) {
      query = query.lte('timestamp', endDate);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[Widget Tracking] Error fetching usage stats:', error);
      return { totalLoads: 0, totalDeployments: 0, uniqueSites: 0, sites: [] };
    }

    const loads = (data || []).filter((r: any) => r.event_type === 'widget_load');
    const deployments = (data || []).filter((r: any) => r.event_type === 'token_deployed');

    // Group by embedding site
    const siteMap = new Map<string, { loads: number; deployments: number }>();

    (data || []).forEach((record: any) => {
      const site = record.embedding_site || 'unknown';
      if (!siteMap.has(site)) {
        siteMap.set(site, { loads: 0, deployments: 0 });
      }
      const stats = siteMap.get(site)!;
      if (record.event_type === 'widget_load') {
        stats.loads++;
      } else if (record.event_type === 'token_deployed') {
        stats.deployments++;
      }
    });

    const sites = Array.from(siteMap.entries()).map(([site, stats]) => ({
      site,
      ...stats
    }));

    return {
      totalLoads: loads.length,
      totalDeployments: deployments.length,
      uniqueSites: siteMap.size,
      sites
    };
  } catch (error) {
    console.error('[Widget Tracking] Exception fetching usage stats:', error);
    return { totalLoads: 0, totalDeployments: 0, uniqueSites: 0, sites: [] };
  }
};

// ============================================
// TOKENS_USD1 TABLE FUNCTIONS (WLFI/Ethereum Mainnet)
// Matches the structure of the main tokens table
// ============================================

export interface USD1TokenData {
  id?: string; // UUID
  name: string;
  symbol: string;
  description?: string;
  website?: string;
  twitter?: string;
  telegram?: string;
  discord?: string;
  logo_url?: string;
  cover_photo_url?: string;
  contract_address: string;
  network?: string; // defaults to 'ethereum'
  pool_address?: string;
  nft_token_id?: string;
  deployer_address: string;
  initial_buy_amount?: string;
  created_at?: string;
  updated_at?: string;
  favorites_count?: number;
  creator_username?: string;
  category?: string;
  dex?: string;
  base_token?: 'USD1' | 'WETH' | 'USDC';
  tx_hash?: string;
}

/**
 * Create a new USD1-paired token record
 */
export const createUSD1Token = async (
  tokenData: Omit<USD1TokenData, 'id' | 'created_at' | 'updated_at' | 'favorites_count'>
): Promise<{ success: boolean; data?: USD1TokenData; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.TOKENS_USD1)
      .insert({
        name: tokenData.name,
        symbol: tokenData.symbol,
        description: tokenData.description || null,
        website: tokenData.website || null,
        twitter: tokenData.twitter || null,
        telegram: tokenData.telegram || null,
        discord: tokenData.discord || null,
        logo_url: tokenData.logo_url || null,
        cover_photo_url: tokenData.cover_photo_url || null,
        contract_address: tokenData.contract_address,
        network: 'ethereum',
        pool_address: tokenData.pool_address || null,
        nft_token_id: tokenData.nft_token_id || null,
        deployer_address: tokenData.deployer_address,
        initial_buy_amount: tokenData.initial_buy_amount || null,
        creator_username: tokenData.creator_username || null,
        category: tokenData.category || null,
        dex: tokenData.dex || 'Uniswap V3',
        base_token: tokenData.base_token || 'USD1',
        tx_hash: tokenData.tx_hash || null,
      })
      .select()
      .single();

    if (error) {
      console.error('[USD1 Token] Error creating token:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ [USD1 Token] Token created successfully:', data?.symbol);
    return { success: true, data };
  } catch (error) {
    console.error('[USD1 Token] Exception creating token:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

/**
 * Fetch all USD1-paired tokens
 */
export const getUSD1Tokens = async (options?: {
  basePair?: 'USD1' | 'WETH' | 'USDC' | 'ALL';
  category?: string;
  limit?: number;
  orderBy?: 'created_at' | 'market_cap_usd';
  ascending?: boolean;
}): Promise<USD1TokenData[]> => {
  try {
    let query = supabase
      .from(TABLES.TOKENS_USD1)
      .select('*');

    // Filter by base pair
    if (options?.basePair && options.basePair !== 'ALL') {
      query = query.eq('base_token', options.basePair);
    }

    // Filter by category
    if (options?.category) {
      query = query.eq('category', options.category);
    }

    // Order
    const orderColumn = options?.orderBy || 'created_at';
    const ascending = options?.ascending ?? false;
    query = query.order(orderColumn, { ascending });

    // Limit
    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[USD1 Token] Error fetching tokens:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('[USD1 Token] Exception fetching tokens:', error);
    return [];
  }
};

/**
 * Get a single USD1 token by contract address
 */
export const getUSD1TokenByAddress = async (
  contractAddress: string
): Promise<USD1TokenData | null> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.TOKENS_USD1)
      .select('*')
      .eq('contract_address', contractAddress.toLowerCase())
      .single();

    if (error) {
      if (error.code !== 'PGRST116') { // Not found is OK
        console.error('[USD1 Token] Error fetching token:', error);
      }
      return null;
    }

    return data;
  } catch (error) {
    console.error('[USD1 Token] Exception fetching token:', error);
    return null;
  }
};

/**
 * Update a USD1 token record
 */
export const updateUSD1Token = async (
  contractAddress: string,
  updates: Partial<USD1TokenData>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from(TABLES.TOKENS_USD1)
      .update(updates)
      .eq('contract_address', contractAddress.toLowerCase());

    if (error) {
      console.error('[USD1 Token] Error updating token:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ [USD1 Token] Token updated successfully');
    return { success: true };
  } catch (error) {
    console.error('[USD1 Token] Exception updating token:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

// ============================================
// TOKENS_ETH TABLE FUNCTIONS (ETH Section/Ethereum Mainnet)
// Similar structure to USD1TokenData but for ETH section tokens
// ============================================

export interface ETHTokenData {
  id?: string; // UUID
  name: string;
  symbol: string;
  description?: string;
  website?: string;
  twitter?: string;
  telegram?: string;
  discord?: string;
  logo_url?: string;
  cover_photo_url?: string;
  contract_address: string;
  network?: string; // defaults to 'ethereum'
  pool_address?: string;
  nft_token_id?: string;
  deployer_address: string;
  initial_buy_amount?: string;
  created_at?: string;
  updated_at?: string;
  favorites_count?: number;
  creator_username?: string;
  category?: string;
  dex?: string;
  base_token?: 'USDC' | 'WETH';
  tx_hash?: string;
}

/**
 * Create a new ETH token record
 */
export const createETHToken = async (
  tokenData: Omit<ETHTokenData, 'id' | 'created_at' | 'updated_at' | 'favorites_count'>
): Promise<{ success: boolean; data?: ETHTokenData; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.TOKENS_ETH)
      .insert({
        name: tokenData.name,
        symbol: tokenData.symbol,
        description: tokenData.description || null,
        website: tokenData.website || null,
        twitter: tokenData.twitter || null,
        telegram: tokenData.telegram || null,
        discord: tokenData.discord || null,
        logo_url: tokenData.logo_url || null,
        cover_photo_url: tokenData.cover_photo_url || null,
        contract_address: tokenData.contract_address,
        network: 'ethereum',
        pool_address: tokenData.pool_address || null,
        nft_token_id: tokenData.nft_token_id || null,
        deployer_address: tokenData.deployer_address,
        initial_buy_amount: tokenData.initial_buy_amount || null,
        creator_username: tokenData.creator_username || null,
        category: tokenData.category || null,
        dex: tokenData.dex || 'Uniswap V3',
        base_token: tokenData.base_token || 'USDC',
        tx_hash: tokenData.tx_hash || null,
      })
      .select()
      .single();

    if (error) {
      console.error('[ETH Token] Error creating token:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ [ETH Token] Token created successfully:', data?.symbol);
    return { success: true, data };
  } catch (error) {
    console.error('[ETH Token] Exception creating token:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

/**
 * Fetch all ETH tokens
 */
export const getETHTokens = async (options?: {
  basePair?: 'USDC' | 'WETH' | 'ALL';
  category?: string;
  limit?: number;
  orderBy?: 'created_at' | 'market_cap_usd';
  ascending?: boolean;
}): Promise<ETHTokenData[]> => {
  try {
    let query = supabase
      .from(TABLES.TOKENS_ETH)
      .select('*');

    // Filter by base pair
    if (options?.basePair && options.basePair !== 'ALL') {
      query = query.eq('base_token', options.basePair);
    }

    // Filter by category
    if (options?.category) {
      query = query.eq('category', options.category);
    }

    // Order
    const orderColumn = options?.orderBy || 'created_at';
    const ascending = options?.ascending ?? false;
    query = query.order(orderColumn, { ascending });

    // Limit
    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[ETH Token] Error fetching tokens:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('[ETH Token] Exception fetching tokens:', error);
    return [];
  }
};

/**
 * Get a single ETH token by contract address
 */
export const getETHTokenByAddress = async (
  contractAddress: string
): Promise<ETHTokenData | null> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.TOKENS_ETH)
      .select('*')
      .eq('contract_address', contractAddress.toLowerCase())
      .single();

    if (error) {
      if (error.code !== 'PGRST116') { // Not found is OK
        console.error('[ETH Token] Error fetching token:', error);
      }
      return null;
    }

    return data;
  } catch (error) {
    console.error('[ETH Token] Exception fetching token:', error);
    return null;
  }
};

/**
 * Update an ETH token record
 */
export const updateETHToken = async (
  contractAddress: string,
  updates: Partial<ETHTokenData>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from(TABLES.TOKENS_ETH)
      .update(updates)
      .eq('contract_address', contractAddress.toLowerCase());

    if (error) {
      console.error('[ETH Token] Error updating token:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ [ETH Token] Token updated successfully');
    return { success: true };
  } catch (error) {
    console.error('[ETH Token] Exception updating token:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

// ============================================
// TOKENS_SOL TABLE FUNCTIONS (Solana)
// ============================================

export interface SolanaTokenData {
  id?: string;
  name: string;
  symbol: string;
  description?: string;
  website?: string;
  twitter?: string;
  telegram?: string;
  discord?: string;
  logo_url?: string;
  mint_address: string;  // Solana uses mint address
  contract_address: string;  // Same as mint_address for compatibility
  network?: string;  // 'sol'
  pool_address?: string;  // Orca Whirlpool address
  deployer_address: string;
  initial_buy_amount?: string;
  created_at?: string;
  updated_at?: string;
  favorites_count?: number;
  creator_username?: string | null;
  decimals?: number;
  dex?: string;  // 'Orca'
  agent_name?: string | null;
  moltbook_handle?: string | null;
  is_agent?: boolean;
  // Cached pool data for efficient swaps (reduces RPC calls)
  token_vault_a?: string;  // SOL vault address
  token_vault_b?: string;  // Token vault address
  tick_spacing?: number;   // Usually 128 for 1% fee tier
}

/**
 * Fetch all Solana tokens
 */
export const getSolanaTokens = async (options?: {
  limit?: number;
  orderBy?: 'created_at' | 'favorites_count';
  ascending?: boolean;
}): Promise<SolanaTokenData[]> => {
  try {
    let query = supabase
      .from(TABLES.TOKENS_SOL)
      .select('*');

    // Order
    const orderColumn = options?.orderBy || 'created_at';
    const ascending = options?.ascending ?? false;
    query = query.order(orderColumn, { ascending });

    // Limit
    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[Solana Token] Error fetching tokens:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('[Solana Token] Exception fetching tokens:', error);
    return [];
  }
};

/**
 * Get a single Solana token by mint address
 */
export const getSolanaTokenByAddress = async (
  mintAddress: string
): Promise<SolanaTokenData | null> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.TOKENS_SOL)
      .select('*')
      .eq('mint_address', mintAddress)
      .single();

    if (error) {
      if (error.code !== 'PGRST116') {
        console.error('[Solana Token] Error fetching token:', error);
      }
      return null;
    }

    return data;
  } catch (error) {
    console.error('[Solana Token] Exception fetching token:', error);
    return null;
  }
};

/**
 * Create a new Solana token record
 */
export const createSolanaToken = async (
  tokenData: Omit<SolanaTokenData, 'id' | 'created_at' | 'updated_at' | 'favorites_count'>
): Promise<{ success: boolean; data?: SolanaTokenData; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.TOKENS_SOL)
      .insert({
        name: tokenData.name,
        symbol: tokenData.symbol,
        description: tokenData.description || null,
        website: tokenData.website || null,
        twitter: tokenData.twitter || null,
        telegram: tokenData.telegram || null,
        discord: tokenData.discord || null,
        logo_url: tokenData.logo_url || null,
        mint_address: tokenData.mint_address,
        contract_address: tokenData.contract_address || tokenData.mint_address,
        network: 'sol',
        pool_address: tokenData.pool_address || null,
        deployer_address: tokenData.deployer_address,
        initial_buy_amount: tokenData.initial_buy_amount || null,
        decimals: tokenData.decimals || 9,
        dex: tokenData.dex || 'Orca',
        // Cached pool data for efficient swaps
        token_vault_a: tokenData.token_vault_a || null,
        token_vault_b: tokenData.token_vault_b || null,
        tick_spacing: tokenData.tick_spacing || 128,
      })
      .select()
      .single();

    if (error) {
      console.error('[Solana Token] Error creating token:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ [Solana Token] Token created successfully:', data?.symbol);
    return { success: true, data };
  } catch (error) {
    console.error('[Solana Token] Exception creating token:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

/**
 * Update a Solana token record
 */
export const updateSolanaToken = async (
  mintAddress: string,
  updates: Partial<SolanaTokenData>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from(TABLES.TOKENS_SOL)
      .update(updates)
      .eq('mint_address', mintAddress);

    if (error) {
      console.error('[Solana Token] Error updating token:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ [Solana Token] Token updated successfully');
    return { success: true };
  } catch (error) {
    console.error('[Solana Token] Exception updating token:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

/**
 * Fetch sentry group photos from Supabase
 * Returns a map of chat_id -> pfp_url
 */
export const getSentryGroupPhotos = async (
  chatIds: number[]
): Promise<Map<number, string | null>> => {
  try {
    if (!chatIds || chatIds.length === 0) {
      return new Map();
    }

    const { data, error } = await supabase
      .from('sentry_groups')
      .select('chat_id, pfp_url')
      .in('chat_id', chatIds);

    if (error) {
      console.error('[Sentry Groups] Error fetching group photos:', error);
      return new Map();
    }

    const photoMap = new Map<number, string | null>();
    if (data) {
      for (const group of data) {
        photoMap.set(group.chat_id, group.pfp_url || null);
      }
    }

    return photoMap;
  } catch (error) {
    console.error('[Sentry Groups] Exception fetching group photos:', error);
    return new Map();
  }
};